import { useMemo } from 'react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/shadcn/tooltip';

export interface ViewerPresenceItem {
  name: string;
}

export interface ViewersPresenceProps {
  viewers: ReadonlyArray<ViewerPresenceItem>;
  maxVisible?: number;
  className?: string;
}

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') {
    return '??';
  }
  const [firstToken] = trimmed.split(/\s+/);
  const token = firstToken ?? trimmed;
  return token.slice(0, 2).toUpperCase();
}

export function ViewersPresence({ viewers, maxVisible = 5, className }: ViewersPresenceProps) {
  const { visibleViewers, overflowCount } = useMemo(() => {
    const safeMax = Math.max(0, maxVisible);
    const visible = viewers.slice(0, safeMax);
    return {
      visibleViewers: visible,
      overflowCount: Math.max(0, viewers.length - visible.length),
    };
  }, [maxVisible, viewers]);

  if (visibleViewers.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex items-center -space-x-2', className)} aria-label="Active viewers">
      {visibleViewers.map((viewer) => {
        const initials = getInitials(viewer.name);
        return (
          <Tooltip key={viewer.name}>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className={cn(
                  'inline-flex h-7 w-7 items-center justify-center rounded-full',
                  'bg-muted text-[10px] font-semibold text-foreground',
                  'ring-1 ring-amber-200 dark:ring-amber-800',
                  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                )}
                aria-label={viewer.name}
              >
                {initials}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>
              {viewer.name}
            </TooltipContent>
          </Tooltip>
        );
      })}

      {overflowCount > 0 ? (
        <span
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-full',
            'bg-muted text-[10px] font-semibold text-foreground',
            'ring-1 ring-amber-200 dark:ring-amber-800'
          )}
          aria-label={`${overflowCount} more viewers`}
        >
          +{overflowCount}
        </span>
      ) : null}
    </div>
  );
}
